import { useEffect, useState } from "react";
import {
  Sparkles, Volume2, VolumeX, Loader2, ChevronDown, ChevronUp, Send,
  History, X, Paperclip,
} from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { api } from "@/lib/api";
import { playQuizWord } from "@/lib/quizAudio";

const JARVIS_IMG = "https://static.prod-images.emergentagent.com/jobs/47440676-455b-415d-a656-32c4bba91c19/images/e7c5bee9ca26687529352ae6a149086b13e9babdbdee8f65009e93b70ca1b38d.png";

export function JarvisCard({
  jarvisCardRef, chatBottomRef,
  autoSpeakReplies, setAutoSpeakReplies,
  voicePickerOpen, setVoicePickerOpen,
  VOICES, voice,
  jarvis, jarvisLoading, askJarvis,
  speakText, loadingTtsKey, playingKey,
  chatHistory, chatSending,
  chatInput, setChatInput, sendChat, handleVoiceTranscript,
  chatFileRef, chatUploading, handleChatFileUpload,
  focusJarvisCard,
  onFlightClick,
}) {
  // Imploderat default. Kommer ihåg val via localStorage.
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("jarvis_card_expanded") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("jarvis_card_expanded", expanded ? "1" : "0"); } catch (_) {}
  }, [expanded]);

  // History-drawer
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/jarvis/history?limit=20");
      setHistoryItems(data?.items || []);
    } catch {
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (historyOpen) loadHistory();
  }, [historyOpen]);

  // Kompakt vy — bara pillrad med avatar + label + ↕-pil
  if (!expanded) {
    return (
      <button
        type="button"
        ref={jarvisCardRef}
        onClick={() => { setExpanded(true); setTimeout(() => focusJarvisCard && focusJarvisCard(), 50); }}
        className="w-full rounded-2xl mb-4 fade-up flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-[color:var(--bg-elevated)] transition-colors text-left"
        style={{
          background: "linear-gradient(135deg, rgba(61,90,128,0.06) 0%, rgba(61,90,128,0.01) 100%)",
          border: "1px solid rgba(61,90,128,0.18)",
        }}
        data-testid="jarvis-card-collapsed"
        aria-expanded="false"
        aria-label="Öppna Edit"
      >
        <img
          src={JARVIS_IMG}
          alt="Edit"
          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
          style={{ background: "rgba(61,90,128,0.1)" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Sparkles size={11} className="text-[color:var(--jarvis)]" />
            <span className="text-[11px] font-semibold text-[color:var(--jarvis)] tracking-wide">EDIT</span>
          </div>
          <p className="text-xs text-[color:var(--text-secondary)] truncate">
            {jarvis ? jarvis.split("\n")[0].slice(0, 70) + (jarvis.length > 70 ? "…" : "") : "Tryck för att fråga eller få dagens sammanfattning"}
          </p>
        </div>
        <ChevronDown size={16} className="text-[color:var(--text-tertiary)] flex-shrink-0" />
      </button>
    );
  }

  return (
    <div
      ref={jarvisCardRef}
      className="rounded-2xl p-4 mb-4 fade-up relative overflow-hidden bg-white"
      style={{
        background: "linear-gradient(135deg, rgba(61,90,128,0.06) 0%, rgba(61,90,128,0.01) 100%)",
        border: "1px solid rgba(61,90,128,0.18)",
      }}
      data-testid="jarvis-card"
    >
      <div className="flex items-start gap-3">
        <img
          src={JARVIS_IMG}
          alt="Edit"
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          style={{ background: "rgba(61,90,128,0.1)" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5">
              <Sparkles size={14} className="text-[color:var(--jarvis)]" />
              <span className="text-xs font-semibold text-[color:var(--jarvis)] tracking-wide">EDIT</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setAutoSpeakReplies(!autoSpeakReplies)}
                className={`p-1.5 rounded-full text-xs border transition-colors ${
                  autoSpeakReplies
                    ? "bg-[color:var(--jarvis)] text-white border-[color:var(--jarvis)]"
                    : "bg-white text-[color:var(--text-tertiary)] border-[color:var(--border)]"
                }`}
                title={autoSpeakReplies ? "Auto-uppläsning på" : "Auto-uppläsning av"}
                data-testid="jarvis-autospeak-toggle"
              >
                <Volume2 size={11} />
              </button>
              <button
                onClick={() => setVoicePickerOpen(true)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-full text-[10px] border-2 border-[color:var(--jarvis)]/30 bg-white text-[color:var(--jarvis)] font-medium"
                title="Byt röst"
                data-testid="jarvis-voice-picker"
              >
                {VOICES.find((v) => v.id === voice)?.label}
                <ChevronDown size={9} />
              </button>
              <button
                onClick={() => setHistoryOpen(true)}
                className="p-1.5 rounded-full bg-white text-[color:var(--text-tertiary)] border border-[color:var(--border)] hover:text-[color:var(--jarvis)]"
                title="Visa de senaste 20 chatt-meddelandena"
                aria-label="Historik"
                data-testid="jarvis-history-btn"
              >
                <History size={11} />
              </button>
              <button
                onClick={() => setExpanded(false)}
                className="p-1.5 rounded-full bg-white text-[color:var(--text-tertiary)] border border-[color:var(--border)] hover:text-[color:var(--jarvis)]"
                title="Minimera Edit"
                aria-label="Minimera"
                data-testid="jarvis-collapse-btn"
              >
                <ChevronUp size={11} />
              </button>
            </div>
          </div>
          {jarvis ? (
            <>
              <p className="text-sm text-[color:var(--text-primary)] whitespace-pre-line leading-relaxed">
                {jarvis}
              </p>
              <button
                onClick={() => speakText("briefing", jarvis)}
                disabled={loadingTtsKey === "briefing"}
                className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[color:var(--text-tertiary)] hover:text-[color:var(--jarvis)] disabled:opacity-50"
                data-testid="jarvis-tts-briefing"
              >
                {loadingTtsKey === "briefing" ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : playingKey === "briefing" ? (
                  <VolumeX size={10} />
                ) : (
                  <Volume2 size={10} />
                )}
                {playingKey === "briefing" ? "Stoppa" : "Läs upp"}
              </button>
            </>
          ) : (
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              Fråga om läxor, prov, kalendern, mail eller annat — eller be om dagens sammanfattning.
            </p>
          )}
          <button
            onClick={askJarvis}
            disabled={jarvisLoading}
            className="mt-2 text-xs font-medium text-[color:var(--jarvis)] hover:underline"
            data-testid="jarvis-summary-button"
          >
            {jarvisLoading ? "Tänker…" : jarvis ? "Uppdatera sammanfattning" : "Be Edit sammanfatta"}
          </button>
        </div>
      </div>

      {/* Chat-history */}
      {chatHistory.length > 0 && (
        <div
          className="mt-3 max-h-[320px] overflow-y-auto space-y-2 pr-1"
          data-testid="jarvis-chat-history"
        >
          {chatHistory.map((m, i) => (
            <div
              key={`msg-${i}-${m.role}-${(m.text || '').slice(0,24)}`}
              className={`text-sm rounded-xl px-3 py-2 leading-relaxed whitespace-pre-line ${
                m.role === "user"
                  ? "bg-[color:var(--jarvis)] text-white ml-6"
                  : "bg-white text-[color:var(--text-primary)] mr-6 border border-[color:var(--border)]"
              }`}
              data-testid={`jarvis-chat-${m.role}-${i}`}
            >
              {m.role === "assistant"
                ? renderJarvisMessage(m.text, onFlightClick)
                : m.text}
              {m.role === "assistant" && (
                <div className="mt-1.5 flex items-center justify-end gap-3">
                  {m.ttsWord && (
                    <button
                      onClick={() => playQuizWord(m.ttsWord)}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[color:var(--jarvis)] hover:opacity-75"
                      data-testid={`quiz-word-tts-${i}`}
                    >
                      <Volume2 size={10} /> Uttala &quot;{m.ttsWord}&quot;
                    </button>
                  )}
                  <button
                    onClick={() => speakText(`chat-${i}`, m.text)}
                    disabled={loadingTtsKey === `chat-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] text-[color:var(--text-tertiary)] hover:text-[color:var(--jarvis)] disabled:opacity-50"
                    data-testid={`jarvis-tts-chat-${i}`}
                  >
                    {loadingTtsKey === `chat-${i}` ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : playingKey === `chat-${i}` ? (
                      <VolumeX size={10} />
                    ) : (
                      <Volume2 size={10} />
                    )}
                    {playingKey === `chat-${i}` ? "Stoppa" : "Läs upp"}
                  </button>
                </div>
              )}
              {m.role === "assistant" && Array.isArray(m.sources) && m.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[color:var(--border)] space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">🌐 Webbkällor</p>
                  {m.sources.map((s, j) => (
                    <a
                      key={s.url || `src-${i}-${j}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-[color:var(--jarvis)] hover:underline truncate"
                      data-testid={`jarvis-chat-source-${i}-${j}`}
                    >
                      [{j + 1}] {s.title}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          {chatSending && (
            <div
              className="text-sm rounded-xl px-3 py-2 bg-white mr-6 text-[color:var(--text-tertiary)] border border-[color:var(--border)] italic"
              data-testid="jarvis-chat-thinking"
            >
              Edit tänker…
            </div>
          )}
          <div ref={chatBottomRef} />
        </div>
      )}

      {/* Chat-input */}
      <form
        onSubmit={sendChat}
        className="mt-3 flex items-center gap-2"
        data-testid="jarvis-chat-form"
      >
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onFocus={focusJarvisCard}
          placeholder="Fråga Edit något…"
          disabled={chatSending}
          className="flex-1 min-w-0 px-3 py-2 text-sm rounded-full border border-[color:var(--border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--jarvis)] focus:ring-offset-0 disabled:opacity-60"
          data-testid="jarvis-chat-input"
        />
        <input
          type="file"
          ref={chatFileRef}
          className="hidden"
          accept=".pdf,.docx,.txt,.md,.csv,image/*,audio/*"
          onChange={handleChatFileUpload}
          data-testid="jarvis-chat-file-input"
        />
        <button
          type="button"
          onClick={() => chatFileRef?.current?.click()}
          disabled={chatSending}
          className="w-9 h-9 rounded-full border border-[color:var(--border)] bg-white text-[color:var(--text-secondary)] flex items-center justify-center disabled:opacity-40 hover:bg-[color:var(--bg-elevated)] transition flex-shrink-0"
          data-testid="jarvis-chat-upload-btn"
          aria-label="Ladda upp fil"
        >
          {chatUploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
        </button>
        <MicButton onTranscript={handleVoiceTranscript} disabled={chatSending} />
        <button
          type="submit"
          disabled={chatSending || !chatInput.trim()}
          className="w-9 h-9 rounded-full bg-[color:var(--jarvis)] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity flex-shrink-0"
          data-testid="jarvis-chat-send"
          aria-label="Skicka"
        >
          <Send size={15} />
        </button>
      </form>
      {historyOpen && (
        <HistoryDrawer
          items={historyItems}
          loading={historyLoading}
          onClose={() => setHistoryOpen(false)}
          onRefresh={loadHistory}
        />
      )}
    </div>
  );
}

function HistoryDrawer({ items, loading, onClose, onRefresh }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
      onClick={onClose}
      data-testid="jarvis-history-drawer"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[80vh] rounded-t-2xl sm:rounded-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <History size={16} className="text-[color:var(--jarvis)]" />
            <h3 className="text-sm font-semibold">Senaste 20 chatt-meddelanden</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[color:var(--bg-elevated)]"
            data-testid="jarvis-history-close"
            aria-label="Stäng"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}
          {!loading && items.length === 0 && (
            <p className="text-sm text-[color:var(--text-secondary)] text-center py-8">
              Inga tidigare meddelanden ännu.
            </p>
          )}
          {items.map((item, i) => (
            <div
              key={i}
              className="rounded-lg border border-[color:var(--border)] overflow-hidden"
              data-testid="jarvis-history-item"
            >
              <div className="bg-[color:var(--bg-elevated)] px-3 py-2 text-xs">
                <span className="font-semibold">Du</span>
                <span className="text-[color:var(--text-tertiary)] ml-2">
                  {item.created_at?.slice(0, 16).replace("T", " ")}
                </span>
                <p className="mt-1 text-sm text-[color:var(--text-primary)]">{item.message}</p>
              </div>
              {item.reply && (
                <div className="px-3 py-2 text-xs bg-white">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={11} className="text-[color:var(--jarvis)]" />
                    <span className="font-semibold text-[color:var(--jarvis)]">Edit</span>
                    {item.tool && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--jarvis)]/10 text-[color:var(--jarvis)] font-mono">
                        {item.tool}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-[color:var(--text-primary)] whitespace-pre-line">
                    {item.reply}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="p-3 border-t flex justify-end">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--jarvis)] disabled:opacity-50"
            data-testid="jarvis-history-refresh"
          >
            Uppdatera
          </button>
        </div>
      </div>
    </div>
  );
}

// Flight-nummer pattern: 2-3 versaler ELLER 1 versal + 1 siffra (D8 Norwegian),
// följt av 1-4 siffror + ev. suffix-bokstav (SK1407, FR7377, D85601, BA0001B).
const FLIGHT_RE = /(?:`|\*\*)?([A-Z][0-9]\d{1,4}[A-Z]?|[A-Z]{2,3}\d{1,4}[A-Z]?)(?:`|\*\*)?/g;
const FALSE_POSITIVES = new Set([
  "PNG", "PDF", "URL", "API", "GMT", "UTC", "CSV", "USA", "GBR", "EUR", "SEK",
]);

function renderJarvisMessage(text, onFlightClick) {
  if (!text) return null;
  // Extrahera datum-kontext — försök i prioriteringsordning:
  // 1) ISO YYYY-MM-DD (entydigt)
  // 2) Svensk D/M, t.ex. "19/6" eller "den 19/6"
  // 3) "D månadsnamn", t.ex. "19 juni"
  // Datum-kontexten används för alla flightnummer i samma svar så
  // klick på t.ex. SK1806 i "Flyg 19/6 16:45 SK1806..." får rätt datum
  // i bevakningen (annars default:ar backend till idag).
  let dateHint = null;
  const iso = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (iso) {
    dateHint = iso[1];
  } else {
    const dm = text.match(/\b(\d{1,2})\/(\d{1,2})(?!\d)/);
    if (dm) {
      dateHint = `${dm[1]}/${dm[2]}`;
    } else {
      const sv = text.match(/\b(\d{1,2})(?:\:e|e)?\s+(januari|februari|mars|april|maj|juni|juli|augusti|september|oktober|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|okt|nov|dec)\b/i);
      if (sv) {
        dateHint = `${sv[1]} ${sv[2].toLowerCase()}`;
      }
    }
  }
  const parts = [];
  let lastIdx = 0;
  let m;
  FLIGHT_RE.lastIndex = 0;
  while ((m = FLIGHT_RE.exec(text)) !== null) {
    const start = m.index;
    const end = FLIGHT_RE.lastIndex;
    const code = m[1];
    if (FALSE_POSITIVES.has(code)) continue;
    if (start > lastIdx) {
      parts.push({ type: "text", value: text.slice(lastIdx, start) });
    }
    parts.push({ type: "flight", value: code });
    lastIdx = end;
  }
  if (lastIdx < text.length) {
    parts.push({ type: "text", value: text.slice(lastIdx) });
  }
  return parts.map((p, i) => {
    if (p.type === "flight") {
      return (
        <button
          key={`f-${i}-${p.value}`}
          type="button"
          onClick={(e) => { e.preventDefault(); onFlightClick?.(p.value, dateHint); }}
          className="inline-flex items-baseline font-mono font-bold text-[color:var(--jarvis)] underline decoration-dotted underline-offset-2 hover:bg-[color:var(--jarvis)] hover:text-white px-1 rounded-md transition-colors cursor-pointer"
          data-testid={`jarvis-flight-link-${p.value}`}
          title={`Bevaka ${p.value}${dateHint ? " (" + dateHint + ")" : ""}`}
        >
          {p.value}
        </button>
      );
    }
    return renderInlineBold(p.value, `t-${i}`);
  });
}

function renderInlineBold(text, keyBase) {
  if (!text) return null;
  // Stöd för markdown-länkar `[text](url)` + **bold** i samma sträng.
  // Hittar länkar FÖRST (har högre precedens), bold inom de delar som inte är länkar.
  const out = [];
  const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIdx = 0;
  let lm;
  let key = 0;
  while ((lm = LINK_RE.exec(text)) !== null) {
    if (lm.index > lastIdx) {
      out.push(...renderBoldOnly(text.slice(lastIdx, lm.index), `${keyBase}-${key++}`));
    }
    const url = lm[2];
    const isExternal = /^https?:\/\//i.test(url);
    out.push(
      <a
        key={`${keyBase}-l-${key++}`}
        href={url}
        target={isExternal ? "_blank" : "_self"}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className="text-[color:var(--jarvis)] underline hover:no-underline font-medium"
        data-testid="jarvis-link"
      >
        {lm[1]}
      </a>
    );
    lastIdx = lm.index + lm[0].length;
  }
  if (lastIdx < text.length) {
    out.push(...renderBoldOnly(text.slice(lastIdx), `${keyBase}-${key++}`));
  }
  return out;
}

function renderBoldOnly(text, keyBase) {
  const segs = text.split(/(\*\*[^*]+\*\*)/g);
  return segs.map((s, i) => {
    if (s.startsWith("**") && s.endsWith("**")) {
      return <strong key={`${keyBase}-${i}`}>{s.slice(2, -2)}</strong>;
    }
    return <span key={`${keyBase}-${i}`}>{s}</span>;
  });
}

